import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FlatRuleForm from './FlatRuleForm';
import LmGrowthRuleForm from './LmGrowthRuleForm';
import LmValueRuleForm from './LmValueRuleForm';
import TierBasedRuleForm from './TierBasedRuleForm';
import PctOfTierRuleForm from './PctOfTierRuleForm';
import CountPerTierRuleForm from './CountPerTierRuleForm';
import RulePreview from './RulePreview';

/**
 * Rule editor modal.
 *
 * Props:
 *   open:            boolean
 *   onClose:         () => void
 *   onSave:          async ({ metric, rule_type, rule_value }) => void
 *   initialRule:     existing rule object (or null/undefined for "Add")
 *   defaultMetrics:  optional list of suggested metrics for the selector
 *   scope:           'tenant' | 'agency_override' — affects copy only
 */
const RULE_TYPES = [
  { value: 'flat',                     label: 'Flat value' },
  { value: 'lm_growth_pct',            label: 'Last-month growth %' },
  { value: 'lm_value',                 label: 'Last-month value (as-is)' },
  { value: 'tier_based',               label: 'Tier-based (per T1..T10)' },
  { value: 'upgrade_pct_of_tier',      label: 'Upgrade % of tier' },
  { value: 'maintain_pct_of_tier',     label: 'Maintain % of tier' },
  { value: 'upgrade_count_per_tier',   label: 'Upgrade count per tier' },
  { value: 'maintain_count_per_tier',  label: 'Maintain count per tier' },
];

const RuleEditor = ({
  open,
  onClose,
  onSave,
  initialRule,
  defaultMetrics = [],
  scope = 'tenant',
  takenMetrics = [],
}) => {
  const { t } = useTranslation();

  // Local state
  const [metric, setMetric] = useState('');
  const [ruleType, setRuleType] = useState('flat');
  const [ruleValue, setRuleValue] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Reset / hydrate when opening
  useEffect(() => {
    if (!open) return;
    if (initialRule) {
      setMetric(initialRule.metric || '');
      setRuleType(initialRule.rule_type || 'flat');
      setRuleValue(initialRule.rule_value || {});
    } else {
      setMetric('');
      setRuleType('flat');
      setRuleValue({});
    }
    setError(null);
    setSaving(false);
  }, [open, initialRule]);

  const isEdit = !!initialRule;

  const availableMetrics = useMemo(() => {
    if (isEdit) return defaultMetrics;
    // For Add mode, exclude metrics already configured at this scope
    return defaultMetrics.filter((m) => !takenMetrics.includes(m.id));
  }, [defaultMetrics, takenMetrics, isEdit]);

  const handleRuleTypeChange = (next) => {
    // Reset rule_value to a sane default per type
    setRuleType(next);
    if (next === 'flat' || next === 'upgrade_pct_of_tier' || next === 'maintain_pct_of_tier') {
      setRuleValue({});
    } else if (next === 'lm_growth_pct') {
      setRuleValue({});
    } else if (next === 'lm_value') {
      setRuleValue({});
    } else if (next === 'tier_based' || next === 'upgrade_count_per_tier' || next === 'maintain_count_per_tier') {
      setRuleValue({ per_tier: {} });
    } else {
      setRuleValue({});
    }
  };

  const validate = () => {
    if (!metric || !metric.trim()) {
      return t('admin.objectiveRules.editor.errMetric', 'A metric name is required.');
    }
    if (!ruleType) {
      return t('admin.objectiveRules.editor.errRuleType', 'Choose a rule type.');
    }
    if (ruleType === 'flat') {
      const v = ruleValue?.value;
      if (v === null || v === undefined || Number.isNaN(Number(v))) {
        return t('admin.objectiveRules.editor.errValue', 'Provide a numeric value.');
      }
    }
    if (ruleType === 'lm_growth_pct') {
      const v = ruleValue?.pct;
      if (v === null || v === undefined || Number.isNaN(Number(v))) {
        return t('admin.objectiveRules.editor.errPct', 'Provide a percentage.');
      }
    }
    if (ruleType === 'upgrade_pct_of_tier' || ruleType === 'maintain_pct_of_tier') {
      const v = ruleValue?.value;
      if (v === null || v === undefined || Number.isNaN(Number(v))) {
        return t('admin.objectiveRules.editor.errPct', 'Provide a percentage.');
      }
    }
    if (
      ruleType === 'tier_based' ||
      ruleType === 'upgrade_count_per_tier' ||
      ruleType === 'maintain_count_per_tier'
    ) {
      const perTier = ruleValue?.per_tier || {};
      if (Object.keys(perTier).length === 0) {
        return t(
          'admin.objectiveRules.editor.errTierBased',
          'Provide at least one tier value.'
        );
      }
    }
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await onSave({
        metric: metric.trim(),
        rule_type: ruleType,
        rule_value: ruleValue,
      });
      onClose();
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const renderForm = () => {
    switch (ruleType) {
      case 'flat':
        return (
          <FlatRuleForm value={ruleValue} onChange={setRuleValue} disabled={saving} />
        );
      case 'lm_growth_pct':
        return (
          <LmGrowthRuleForm
            value={ruleValue}
            onChange={setRuleValue}
            disabled={saving}
          />
        );
      case 'lm_value':
        return (
          <LmValueRuleForm
            value={ruleValue}
            onChange={setRuleValue}
            disabled={saving}
          />
        );
      case 'tier_based':
        return (
          <TierBasedRuleForm
            value={ruleValue}
            onChange={setRuleValue}
            disabled={saving}
          />
        );
      case 'upgrade_pct_of_tier':
        return (
          <PctOfTierRuleForm
            value={ruleValue}
            onChange={setRuleValue}
            disabled={saving}
            variant="upgrade"
          />
        );
      case 'maintain_pct_of_tier':
        return (
          <PctOfTierRuleForm
            value={ruleValue}
            onChange={setRuleValue}
            disabled={saving}
            variant="maintain"
          />
        );
      case 'upgrade_count_per_tier':
        return (
          <CountPerTierRuleForm
            value={ruleValue}
            onChange={setRuleValue}
            disabled={saving}
            variant="upgrade"
          />
        );
      case 'maintain_count_per_tier':
        return (
          <CountPerTierRuleForm
            value={ruleValue}
            onChange={setRuleValue}
            disabled={saving}
            variant="maintain"
          />
        );
      default:
        return null;
    }
  };

  const titleKey = isEdit
    ? 'admin.objectiveRules.editor.editTitle'
    : 'admin.objectiveRules.editor.addTitle';
  const titleFallback = isEdit ? 'Edit rule' : 'Add rule';

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-4"
      style={{ backgroundColor: 'rgba(15,23,42,0.55)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full md:max-w-2xl bg-card rounded-t-2xl md:rounded-2xl shadow-elevation-3 border border-border flex flex-col max-h-[95vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3 shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-accent font-semibold">
              {scope === 'agency_override'
                ? t(
                    'admin.objectiveRules.editor.scopeAgency',
                    'Agency override'
                  )
                : t('admin.objectiveRules.editor.scopeTenant', 'Tenant rule')}
            </div>
            <h2 className="font-heading text-lg font-bold text-foreground">
              {t(titleKey, titleFallback)}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
            aria-label={t('common.cancel', 'Cancel')}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Metric selector */}
          <div className="space-y-2">
            <label className="block text-xs uppercase tracking-widest text-muted-foreground font-semibold">
              {t('admin.objectiveRules.editor.metric', 'Metric')}
            </label>
            {isEdit ? (
              <input
                type="text"
                disabled
                value={metric}
                className="w-full px-3 py-2 rounded-lg bg-muted/50 border border-border text-foreground text-base font-data outline-none"
              />
            ) : (
              <>
                <input
                  type="text"
                  list="objective-rule-metrics"
                  value={metric}
                  onChange={(e) => setMetric(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-card border border-border text-foreground text-base font-data outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
                  placeholder="recruited_creators"
                />
                <datalist id="objective-rule-metrics">
                  {availableMetrics.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label}
                    </option>
                  ))}
                </datalist>
                <p className="text-xs text-muted-foreground">
                  {t(
                    'admin.objectiveRules.editor.metricHelp',
                    'Pick from suggestions or type a custom metric name.'
                  )}
                </p>
              </>
            )}
          </div>

          {/* Rule type selector */}
          <div className="space-y-2">
            <label className="block text-xs uppercase tracking-widest text-muted-foreground font-semibold">
              {t('admin.objectiveRules.editor.ruleType', 'Rule type')}
            </label>
            <select
              value={ruleType}
              onChange={(e) => handleRuleTypeChange(e.target.value)}
              disabled={saving}
              className="w-full px-3 py-2 rounded-lg bg-card border border-border text-foreground text-base outline-none focus:border-accent focus:ring-1 focus:ring-accent/30 disabled:opacity-60"
            >
              {RULE_TYPES.map((rt) => (
                <option key={rt.value} value={rt.value}>
                  {t(`admin.objectiveRules.ruleType.${rt.value}`, rt.label)}
                </option>
              ))}
            </select>
          </div>

          {/* Contextual form */}
          <div>{renderForm()}</div>

          {/* Live preview */}
          <RulePreview ruleType={ruleType} ruleValue={ruleValue} metric={metric} />

          {error && (
            <div className="rounded-xl border border-error/30 bg-error/5 px-4 py-2 text-sm text-error">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border bg-surface-2/40 flex items-center justify-end gap-2 shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-card border border-border text-foreground text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {saving
              ? t('admin.objectiveRules.editor.saving', 'Saving…')
              : t('admin.objectiveRules.editor.save', 'Save rule')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RuleEditor;
